/**
 * Privacy Policy Page
 *
 * Displays the privacy policy for RetireEZ application.
 * Accessible from Sign In page and can be viewed by any user.
 *
 * Uses LegalLayout for consistent structure (FS-016 WP-2).
 */

import { Link } from "react-router-dom";
import { LegalLayout } from "../components/LegalLayout";

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      lastUpdated="Last Updated: February 13, 2026"
      id="privacy-policy"
    >
      <section>
        <h2>1. Our Privacy Commitment</h2>
        <p>
          RetireEZ is built with privacy as a core principle. We believe your
          financial data is yours and yours alone. This Privacy Policy explains
          how we collect, use, protect, and handle your personal information
          when you use our retirement planning service.
        </p>
      </section>

      <section>
        <h2>2. Data Storage and Security</h2>
        <p>
          When you sign in with your Google, Apple, or Microsoft account, your
          retirement planning data is securely stored and encrypted. We
          implement strict security measures to protect your privacy and ensure
          your financial information remains confidential.
        </p>
      </section>

      <section>
        <h2>3. Information We Collect</h2>

        <h3>3.1 Information You Provide</h3>
        <p>
          When you use RetireEZ, you provide personal and financial information
          necessary to create your retirement plan. This includes profile
          details, financial data such as asset balances and income sources, and
          planning assumptions like expected rates of return and life
          expectancy. We collect this information to provide accurate retirement
          planning calculations and projections tailored to your situation.
        </p>

        <h3>3.2 Authentication Information</h3>
        <p>
          When you sign in with Google, Apple, or Microsoft, we receive basic
          authentication information including your email address, display name,
          profile photo (optional), a unique user ID, and your year of birth. We
          collect your year of birth to calculate your age, which is essential
          for accurate retirement planning and age-specific financial
          projections.
        </p>

        <h3>3.3 Automatically Collected Information</h3>
        <p>We automatically collect minimal technical information:</p>
        <ul>
          <li>Browser type and version</li>
          <li>Device type (mobile, tablet, desktop)</li>
          <li>Anonymized error logs for debugging purposes</li>
        </ul>
        <p>
          <strong>No Tracking:</strong> We do NOT use cookies for tracking,
          analytics, or advertising. We do NOT track your browsing behavior
          across websites.
        </p>
      </section>

      <section>
        <h2>4. How We Use Your Information</h2>
        <p>We use your information solely to:</p>
        <ul>
          <li>Provide retirement planning calculations and projections</li>
          <li>Store and synchronize your data across devices</li>
          <li>Authenticate your identity when you sign in</li>
          <li>Improve the Service and fix technical issues</li>
          <li>Comply with legal obligations</li>
        </ul>
        <p>
          <strong>We will NEVER:</strong>
        </p>
        <ul>
          <li>Sell your personal data to third parties</li>
          <li>Share your financial data with advertisers</li>
          <li>
            Use your data for marketing purposes without your explicit consent
          </li>
          <li>Track your browsing behavior across the internet</li>
        </ul>
      </section>

      <section>
        <h2>5. Data Security</h2>
        <p>
          We implement industry-standard security practices to protect your
          data:
        </p>
        <ul>
          <li>
            <strong>Encryption:</strong> All data is encrypted at rest and in
            transit using industry-standard protocols
          </li>
          <li>
            <strong>Access Control:</strong> Strict security rules prevent
            unauthorized access to your data
          </li>
          <li>
            <strong>Authentication:</strong> Secure OAuth 2.0 authentication (no
            passwords stored by us)
          </li>
          <li>
            <strong>Data Isolation:</strong> Your data is isolated and
            accessible only to you
          </li>
        </ul>
      </section>

      <section>
        <h2>6. Third-Party Services</h2>
        <p>
          RetireEZ uses third-party services for authentication and data
          storage. When you sign in with Google, Apple, or Microsoft, their
          respective privacy policies apply to the authentication process. You
          can review their privacy policies at:{" "}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google
          </a>
          ,{" "}
          <a
            href="https://www.apple.com/legal/privacy/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Apple
          </a>
          ,{" "}
          <a
            href="https://privacy.microsoft.com/privacystatement"
            target="_blank"
            rel="noopener noreferrer"
          >
            Microsoft
          </a>
          .
        </p>
      </section>

      <section>
        <h2>7. Your Privacy Rights</h2>
        <p>
          As a Canadian resident, you have the following rights under privacy
          legislation (including PIPEDA):
        </p>
        <ul>
          <li>
            <strong>Access:</strong> Request a copy of your personal data
          </li>
          <li>
            <strong>Correction:</strong> Update or correct inaccurate
            information
          </li>
          <li>
            <strong>Deletion:</strong> Request deletion of your account and all
            associated data
          </li>
          <li>
            <strong>Export:</strong> Download your data in JSON format
          </li>
        </ul>
        <p>
          To exercise these rights, access the{" "}
          <Link to="/dashboard">Settings page</Link> when signed in, or contact
          us at <a href="mailto:support@retire-ez.ca">support@retire-ez.ca</a>.
        </p>
      </section>

      <section>
        <h2>8. Data Retention</h2>
        <p>
          Your data is retained as long as your account is active. If you delete
          your account, all data is permanently deleted within 30 days. You can
          request immediate deletion by contacting us at{" "}
          <a href="mailto:support@retire-ez.ca">support@retire-ez.ca</a>.
        </p>
      </section>

      <section>
        <h2>9. Children&apos;s Privacy</h2>
        <p>
          RetireEZ is not intended for use by individuals under the age of 18.
          We do not knowingly collect personal information from children. If we
          become aware that a child has provided us with personal information,
          we will delete it immediately.
        </p>
      </section>

      <section>
        <h2>10. International Users</h2>
        <p>
          RetireEZ is designed for Canadian residents and complies with Canadian
          privacy laws (PIPEDA). If you access the Service from outside Canada,
          please note that your information may be transferred to and stored in
          Canada and the United States (via Google Cloud Platform).
        </p>
      </section>

      <section>
        <h2>11. Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time to reflect changes
          in our practices or legal requirements. We will notify you of material
          changes by updating the &ldquo;Last Updated&rdquo; date at the top of
          this page and, if you are signed in, via in-app notification. Your
          continued use of the Service after such changes constitutes acceptance
          of the updated Privacy Policy.
        </p>
      </section>

      <section>
        <h2>12. Contact Us</h2>
        <p>
          If you have questions or concerns about this Privacy Policy or how we
          handle your data, please contact us at{" "}
          <a href="mailto:support@retire-ez.ca">support@retire-ez.ca</a>.
        </p>
      </section>
    </LegalLayout>
  );
}
